import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import AddAPhotoOutlinedIcon from '@mui/icons-material/AddAPhotoOutlined';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

import FirebaseImage from '../Media/FirebaseImage';
import { uploadProfileImage } from '../../Utils/uploads';
import { useI18n } from '../../i18n/I18nProvider';

const ProfilePhotoEditor = ({ image, name, role, onUploaded }) => {
  const { pick } = useI18n();
  const inputRef = useRef(null);
  const [preview, setPreview] = useState('');
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const selectFile = () => inputRef.current?.click();

  const handleFile = async (file) => {
    if (!file || uploading) return;
    const nextPreview = URL.createObjectURL(file);
    setPreview(nextPreview);
    setUploading(true);
    setProgress(0);

    try {
      const url = await uploadProfileImage(file, setProgress);
      onUploaded?.(url);
      toast.success(pick('Photo de profil mise à jour.', 'Profile photo updated.'));
    } catch (error) {
      toast.error(error?.message || pick('La photo n’a pas pu être enregistrée.', 'The photo could not be saved.'));
      setPreview('');
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  return (
    <Card>
      <PhotoButton
        type="button"
        onClick={selectFile}
        onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          handleFile(event.dataTransfer.files?.[0]);
        }}
        $dragging={dragging}
        aria-label={pick('Changer la photo de profil', 'Change profile photo')}
      >
        <Avatar
          src={preview || image}
          fallbackSrc="/images/user.svg"
          alt={pick(`Photo de profil de ${name || 'l’utilisateur'}`, `Profile photo of ${name || 'the user'}`)}
        />
        <EditBadge aria-hidden="true"><AddAPhotoOutlinedIcon /></EditBadge>
        {uploading && (
          <ProgressOverlay>
            <strong>{progress}%</strong>
            <span>{pick('Envoi en cours', 'Uploading')}</span>
          </ProgressOverlay>
        )}
      </PhotoButton>

      <Identity>
        <h2>{name || pick('Mon profil', 'My profile')}</h2>
        <Role>{role === 'Client' ? pick('Client', 'Client') : role === 'Artisan' ? pick('Artisan', 'Professional') : role || pick('Membre', 'Member')}</Role>
      </Identity>

      <UploadButton type="button" onClick={selectFile} disabled={uploading}>
        <CloudUploadOutlinedIcon />
        {uploading ? pick(`Envoi ${progress}%`, `Uploading ${progress}%`) : image ? pick('Changer ma photo', 'Change my photo') : pick('Ajouter une photo', 'Add a photo')}
      </UploadButton>

      <Helper>
        <VerifiedUserOutlinedIcon />
        <span>{pick('JPG, PNG ou WebP · 8 Mo maximum. La photo est stockée dans votre espace sécurisé.', 'JPG, PNG or WebP · 8 MB maximum. The photo is stored securely in your account.')}</span>
      </Helper>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        hidden
        onChange={(event) => {
          handleFile(event.target.files?.[0]);
          event.target.value = '';
        }}
      />
    </Card>
  );
};

const Card = styled.aside`
  position: sticky;
  top: 5.75rem;
  align-self: start;
  height: fit-content;
  display: grid;
  grid-auto-rows: max-content;
  align-content: start;
  justify-items: center;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xl);
  background: #fff;
  box-shadow: var(--shadow-sm);

  @media (max-width: 56rem) {
    position: static;
  }
`;

const PhotoButton = styled.button`
  position: relative;
  width: 10rem;
  height: 10rem;
  padding: .35rem;
  border: 2px solid ${({ $dragging }) => ($dragging ? 'var(--color-accent)' : '#fff')};
  border-radius: 2.25rem;
  background: ${({ $dragging }) => ($dragging ? 'var(--color-accentSoft)' : '#f2efea')};
  cursor: pointer;
  box-shadow: 0 1rem 2.5rem rgba(0,0,0,.12);
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease;
  overflow: hidden;

  &:hover { transform: translateY(-3px); }

  @media (max-width: 36rem) {
    width: 8.5rem;
    height: 8.5rem;
    border-radius: 1.9rem;
  }
`;

const Avatar = styled(FirebaseImage)`
  width: 100%;
  height: 100%;
  border-radius: 1.9rem;
  object-fit: cover;
  background: #eeeae4;
`;

const EditBadge = styled.span`
  position: absolute;
  right: .6rem;
  bottom: .6rem;
  width: 2.5rem;
  height: 2.5rem;
  display: grid;
  place-items: center;
  border: 3px solid #fff;
  border-radius: 50%;
  color: #000;
  background: var(--color-accent);
  box-shadow: 0 .5rem 1rem rgba(0,0,0,.18);

  svg { font-size: 1.15rem; }
`;

const ProgressOverlay = styled.div`
  position: absolute;
  inset: .35rem;
  display: grid;
  place-content: center;
  gap: .1rem;
  border-radius: 1.9rem;
  color: #fff;
  background: rgba(0,0,0,.72);
  backdrop-filter: blur(8px);

  strong { font-size: 1.35rem; }
  span { font-size: .72rem; }
`;

const Identity = styled.div`
  text-align: center;
  h2 { margin: 0; font-size: 1.2rem; }
`;

const Role = styled.span`
  display: inline-flex;
  margin-top: .35rem;
  padding: .3rem .62rem;
  border-radius: 999px;
  color: #5f4a2d;
  background: var(--color-accentSoft);
  font-size: .72rem;
  font-weight: 800;
`;

const UploadButton = styled.button`
  width: 100%;
  min-height: 2.9rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .5rem;
  padding: .65rem .9rem;
  border: 1px solid #000;
  border-radius: 999px;
  color: #fff;
  background: #000;
  font: inherit;
  font-size: .82rem;
  font-weight: 800;
  cursor: pointer;

  &:disabled { opacity: .55; cursor: wait; }
  svg { font-size: 1.05rem; }
`;

const Helper = styled.div`
  display: flex;
  align-items: flex-start;
  gap: .55rem;
  color: var(--color-muted);
  font-size: .7rem;
  line-height: 1.45;

  svg { flex: 0 0 auto; margin-top: .1rem; font-size: 1rem; color: var(--color-accentDark); }
`;

export default ProfilePhotoEditor;
